import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Collapse, Drawer, IconButton, Stack, Typography } from '@mui/material';
import hexContrastColor from 'hex-contrast-color';

import useProjectsStore from 'src/stores/projects';
import useChatsStore from 'src/stores/chats';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import EventService from 'src/services/EventService';
import ArrowLeft from 'src/icons/ArrowLeft';
import FormWrapper from 'src/components/FormWrapper';
import CustomStepperItem from 'src/components/CustomStepperItem';
import ROUTES from 'src/router/routes';
import Wallet from 'src/icons/Wallet';
import UserPlus from 'src/icons/UserPlus';
import LinkIcon from 'src/icons/Link';

import useUserStore from 'src/stores/user';

import { history } from 'src/utils/history';

import Logo from 'src/components/Logo';

import { CaseIcon } from 'src/icons/CaseIcon';

import { ProjectsIcon } from 'src/icons/ProjectsIcon';

import useCollectionsStore from 'src/stores/collections';

import { ROUTES_URL } from 'src/constants/routes';

import sessionStore from 'src/stores/session';

import AnimatedNotificationIcon from './AnimatedNotificationIcon';

import MenuButton from './components/MenuButton';
import Book from './Icons/Book';
import Chats from './Icons/Chats';
import MapLayer from './Icons/MapLayer';


import ItemMenu from './components/ItemMenu';
import AccordionMenu, { AccordionButton, AccordionMenuContainer } from './components/AccordionMenu';
import SettingsAccordion from './components/SettingsAccordion';
import LeadStreamsMenu from './components/LeadStreamsMenu';
import ProfileDropdown from './components/ProfileDropdown';
import menuList, { PAGES } from './menuList';


type MenuProps = {
    open: boolean;
    onClose: () => void;
    anchore: 'right' | 'left';
    variant: 'temporary' | 'permanent';
};

const MenuComponent = ({ open, onClose, anchore, variant }: MenuProps) => {
    const isMobile = useIsMobileSize();
    const [collapse, setCollapse] = useState(true);
    const [streamsCollapse, setStreamsCollapse] = useState(true);
    const [forceUpdate, setForceUpdate] = useState(0);

    const { activeProject, projects } = useProjectsStore(({ activeProject, projects }) => ({
        activeProject,
        projects,
    }));
    const { privateCollection } = useCollectionsStore(({ privateCollection }) => ({ privateCollection }));
    const { bonusPoints, current_subscription, isOnboardingCompleted, tg_notifications_enabled, email } = useUserStore(
        ({ bonusPoints, current_subscription, isOnboardingCompleted, tg_notifications_enabled, email }) => ({
            bonusPoints,
            current_subscription,
            isOnboardingCompleted,
            tg_notifications_enabled,
            email,
        })
    );

    // Подсчет общего использования по всем проектам
    const totalUsage = useMemo(() => {
        let totalKeywords = 0;
        let totalChats = 0;
        const uniqueBundlesSet = new Set<number>();
        const uniquePrivateBundlesSet = new Set<number>(); // Для учета уникальных личных подборок

        projects.forEach((project) => {
            // Ключевые слова
            if ('keywords' in project && project.keywords) {
                totalKeywords += project.keywords.length;
            }

            // Чаты из личной подборки проекта
            // Используем Map для учета уникальных подборок, так как одна подборка может быть привязана к нескольким проектам
            if ('bundles' in project && project.bundles) {
                project.bundles.forEach((bundle) => {
                    if (!bundle.is_public && bundle.id) {
                        const bundleId = bundle.id;
                        const chatCount = bundle.chat_count || 0;
                        // Сохраняем максимальное значение chat_count для каждой подборки
                        if (!uniquePrivateBundlesSet.has(bundleId)) {
                            uniquePrivateBundlesSet.add(bundleId);
                            totalChats += chatCount;
                        }
                    }
                });
            }

            // Публичные подборки - используем Set для уникальности
            if ('bundles' in project && project.bundles) {
                project.bundles.forEach((bundle) => {
                    if (bundle.is_public) {
                        uniqueBundlesSet.add(bundle.id);
                    }
                });
            }
        });

        return {
            keywords: totalKeywords,
            chats: totalChats,
            bundles: uniqueBundlesSet.size,
        };
    }, [projects]);

    // Мемоизация данных для оптимизации
    const bundles = useMemo(
        () => activeProject?.bundles.filter((collection) => collection.is_public) || [],
        [activeProject?.bundles]
    );

    const subscriptionInfo = useMemo(
        () => ({
            planName: current_subscription?.plan.name || 'Выбрать тариф',
            keywordsLimit: current_subscription?.plan.keywords_limit || 0,
            chatsLimit: current_subscription?.plan.chats_limit || 0,
            bundlesLimit: current_subscription?.plan.bundles_limit || 0,
            projectsLimit: current_subscription?.plan.projects_limit || 0,
            hasSubscription: !!current_subscription,
            planColor: current_subscription?.plan.color_code || '000000',
        }),
        [current_subscription]
    );

    // Форсированный ререндер при изменении состояния
    useEffect(() => {
        setForceUpdate((v) => v + 1);
    }, [
        isOnboardingCompleted,
        current_subscription,
        bonusPoints,
        tg_notifications_enabled,
        activeProject,
        privateCollection,
        projects,
    ]);

    // Форсированный ререндер при изменении состояния уведомлений для анимации
    useEffect(() => {
        setForceUpdate((v) => v + 1);
    }, [tg_notifications_enabled]);

    // Слушаем событие обновления уведомлений для анимации
    useEffect(() => {
        const handleNotificationsUpdate = (_, detail: { enabled: boolean }) => {
            setForceUpdate((v) => v + 1);
        };

        const subscriber = EventService.subscribe('NOTIFICATIONS_UPDATED', handleNotificationsUpdate);

        return () => {
            subscriber.unsubscribe();
        };
    }, []);

    // Мемоизация обработчиков событий
    const handleTariffClick = useCallback(() => {
        // Если есть активный тариф - идем на страницу тарифа, иначе на оплату
        if (current_subscription?.is_active) {
            history.push(ROUTES_URL.MY_TARIFF);
        } else {
            history.push(ROUTES_URL.PAYMENT);
        }
        EventService.dispatch('CLOSE_MENU');
    }, [current_subscription]);

    const handleKeywordsClick = useCallback(() => {
        history.push(ROUTES.KEYWORDS.path);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleChatsClick = useCallback(() => {
        history.push(ROUTES.CHATS_AND_CHANNELS.path);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleCollectionsClick = useCallback(() => {
        history.push(ROUTES.COLLECTIONS.path);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleProjectsClick = useCallback(() => {
        history.push(ROUTES_URL.PROJECTS);
        EventService.dispatch('CLOSE_MENU');
    }, []);


    const handlePartnershipClick = useCallback(() => {
        history.push(ROUTES_URL.PARTNERSHIP);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleIntegrationsClick = useCallback(() => {
        history.push(ROUTES_URL.INTEGRATIONS);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleAIManagerClick = useCallback(() => {
        history.push(ROUTES_URL.AI_MANAGER);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleNotificationsClick = useCallback(() => {
        history.push(ROUTES_URL.NOTIFICATIONS);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleLogout = useCallback(() => {
        const store = sessionStore.getState();
        EventService.dispatch('CLOSE_MENU');
        history.push(ROUTES_URL.LOGIN);
        store.logout();
    }, []);


    const handleCollapseToggle = useCallback(() => {
        if (isMobile) return;
        setCollapse((old) => !old);
    }, [isMobile]);

    const handleStreamsCollapseToggle = useCallback(() => {
        if (isMobile) return;
        setStreamsCollapse((old) => !old);
    }, [isMobile]);

    const renderButtonsPage = useCallback(() => {
        return (
            <>
                <Box
                    mx={-1}
                    mb={1}
                >
                    <CustomStepperItem
                        showStep={false}
                        step={0}
                        currentStep={0}
                        onClick={handleTariffClick}
                        title="Ваш тариф"
                        chip={
                            <Typography
                                onClick={onClose}
                                component={RouterLink}
                                variant="body2"
                                color="info"
                                style={{
                                    textDecoration: 'none',
                                }}
                                to={current_subscription?.is_active ? ROUTES_URL.MY_TARIFF : ROUTES_URL.PAYMENT}
                            >
                                {subscriptionInfo.hasSubscription && (
                                    <Chip
                                        label={subscriptionInfo.planName}
                                        size="medium"
                                        style={{
                                            backgroundColor: '#' + subscriptionInfo.planColor,
                                            color: hexContrastColor('#' + subscriptionInfo.planColor),
                                        }}
                                    />
                                )}
                                {!subscriptionInfo.hasSubscription && 'Выбрать тариф'}
                            </Typography>
                        }
                        done
                        sx={{
                            marginLeft: '6px',
                            marginRight: '6px',
                        }}
                    />
                </Box>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                >
                    <MenuButton
                        label="Проекты"
                        subLabel={`${projects.length} из ${subscriptionInfo.projectsLimit}`}
                        icon={<ProjectsIcon />}
                        onClick={handleProjectsClick}
                        disabled={!isOnboardingCompleted}
                    />
                    <MenuButton
                        label="Словарь"
                        subLabel={`${totalUsage.keywords} из ${subscriptionInfo.keywordsLimit}`}
                        icon={<Book />}
                        onClick={handleKeywordsClick}
                        disabled={!isOnboardingCompleted}
                    />
                    <MenuButton
                        label="Чаты"
                        subLabel={`${totalUsage.chats} из ${subscriptionInfo.chatsLimit}`}
                        icon={<Chats />}
                        onClick={handleChatsClick}
                        disabled={!isOnboardingCompleted}
                    />
                    <MenuButton
                        label="Подборки"
                        subLabel={`${totalUsage.bundles} из ${subscriptionInfo.bundlesLimit}`}
                        icon={<MapLayer />}
                        onClick={handleCollectionsClick}
                        disabled={!isOnboardingCompleted}
                    />
                </Box>
            </>
        );
    }, [
        activeProject,
        privateCollection,
        bundles,
        subscriptionInfo,
        totalUsage,
        projects,
        isOnboardingCompleted,
        handleTariffClick,
        handleKeywordsClick,
        handleChatsClick,
        handleCollectionsClick,
        handleProjectsClick,
        onClose,
    ]);

    const renderButtonsPageWrapper = useCallback(() => {
        if (isMobile) {
            return (
                <FormWrapper
                    borderRadius={4}
                    sx={{
                        padding: '6px',
                    }}
                >
                    {renderButtonsPage()}
                </FormWrapper>
            );
        }

        return (
            <Box
                border="1px solid #E3ECF3"
                borderRadius={4}
                padding={0.8}
            >
                {renderButtonsPage()}
            </Box>
        );
    }, [isMobile, renderButtonsPage]);

    return (
        <Drawer
            anchor={anchore}
            open={open}
            onClose={onClose}
            variant={variant}
            PaperProps={{
                sx: {
                    position: 'sticky',
                    height: '100dvh',
                    overflow: 'auto',
                    width: '100%',
                    border: 'none',
                    boxShadow: 'none',
                },
            }}
        >
            <Box
                display="flex"
                flexDirection="column"
                width={isMobile ? '100vw' : '100%'}
                height={'100%'}
                overflow="auto"
                bgcolor={isMobile ? 'background.paper' : 'background.default'}
                px={1}
            >
                <Box
                    height={40}
                    display="flex"
                    alignItems="center"
                >
                    {isMobile && (
                        <IconButton onClick={onClose}>
                            <ArrowLeft />
                        </IconButton>
                    )}
                    {!isMobile && (
                        <Box my={1}>
                            <Logo />
                        </Box>
                    )}
                </Box>
                {!isMobile && (
                    <Stack
                        sx={{ gap: 1 }}
                        mb={20 / 8}
                        mt={1.5}
                    >
                        {PAGES.map((page, key) => (
                            <AccordionMenuContainer key={key}>
                                <ItemMenu
                                    title={page.title}
                                    icon={page.icon}
                                    onAction={page.onClick}
                                />
                            </AccordionMenuContainer>
                        ))}
                    </Stack>
                )}
                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                >
                    <>
                        <Box
                            onClick={handleCollapseToggle}
                            style={{
                                cursor: 'pointer',
                            }}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            px={1}
                            mb={isMobile ? 20 / 8 : 1}
                        >
                            <Typography
                                variant={isMobile ? 'h1' : 'h3'}
                                sx={{ fontWeight: 'bold', color: 'black' }}
                            >
                                Мониторинг
                            </Typography>
                            {!isMobile && <AccordionButton isOpen={collapse} />}
                        </Box>
                        <Collapse
                            in={isMobile ? true : collapse}
                            sx={{
                                minHeight: 'auto!important',
                            }}
                        >
                            <Box mb={1}>{renderButtonsPageWrapper()}</Box>
                        </Collapse>
                        <Box
                            onClick={handleStreamsCollapseToggle}
                            style={{
                                cursor: 'pointer',
                            }}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            px={1}
                            mb={isMobile ? 20 / 8 : 1}
                            mt={1}
                        >
                            <Typography
                                variant={isMobile ? 'h1' : 'h3'}
                                sx={{ fontWeight: 'bold', color: 'black' }}
                            >
                                Потоки лидов
                            </Typography>
                            {!isMobile && <AccordionButton isOpen={streamsCollapse} />}
                        </Box>
                        <Collapse
                            in={isMobile ? true : streamsCollapse}
                            sx={{
                                minHeight: 'auto!important',
                            }}
                        >
                            <Box mb={1}>
                                <LeadStreamsMenu />
                            </Box>
                        </Collapse>
                        <AccordionMenuContainer mb={1}>
                            <ItemMenu
                                icon={<CaseIcon />}
                                title="AI-менеджер"
                                action
                                onAction={handleAIManagerClick}
                            />
                        </AccordionMenuContainer>
                        <AccordionMenuContainer mb={1}>
                            <ItemMenu
                                icon={<LinkIcon />}
                                title="Интеграции"
                                action
                                onAction={handleIntegrationsClick}
                            />
                        </AccordionMenuContainer>
                        <AccordionMenuContainer mb={1}>
                            <ItemMenu
                                icon={<UserPlus />}
                                title="Партнёрство"
                                action
                                onAction={handlePartnershipClick}
                            />
                        </AccordionMenuContainer>
                        <AccordionMenuContainer mb={1}>
                            <ItemMenu
                                icon={<AnimatedNotificationIcon isEnabled={!!tg_notifications_enabled} />}
                                title="Уведомления"
                                action
                                onAction={handleNotificationsClick}
                            />
                        </AccordionMenuContainer>
                        <ProfileDropdown
                            email={email}
                            bonusPoints={bonusPoints}
                        />
                    </>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    pb={3}
                >
                    <Typography
                        textAlign="center"
                        variant="body2"
                        color="secondary"
                    >
                        2024–2026 © ТГХ, Версия 4.06.48
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
};

export default React.memo(MenuComponent);
